/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { Channel, Periods } from 'cio/utils/metrics/common';
import {
  MEASURES as ANALYSIS_MEASURE_ORDER,
  DELIVERY_METRICS_MEASURES,
  JOURNEY_METRICS_MEASURES,
  ProcessedMetricItem,
  SimpleCampaignNewsletter,
  MetricItem,
  MEASURE_LABELS,
  AnalysisDataType,
} from 'cio/utils/metrics/analysis';
import { measureDenominators, MeasureType } from 'cio/utils/metrics/measure';
import moment from 'moment-timezone';
import { MAX_CUSTOM_DAYS_COUNT, LAST_30_DAYS_RANGE_PRESET_UTC } from 'cio/utils/workflow-metrics';
import actionInfo, { actionTypeName, deliveryStatesAndActionsPerType } from 'cio/utils/action-info';
import { ActionType } from 'cio/models/action';
import { inject as service } from '@ember/service';
import EnvironmentService from 'cio/services/environment';
import { task } from 'ember-concurrency';
import DS from 'ember-data';
import MetricsService from 'cio/services/metrics';
import { CampaignState, CampaignType } from 'cio/models/campaign';
import ApiService from 'cio/services/api';
import { isEmpty } from '@ember/utils';
import AnalyticsService from 'cio/services/analytics';
import config from 'cio/config/environment';
import { debounce } from '@ember/runloop';
import AccountService from 'cio/services/account';
import ExportsService from 'cio/services/exports';
import AuthService from 'cio/services/auth';
import { SortDirection } from 'cio/utils/sort';
import { unixNowSeconds } from 'cio/utils/time';
import { MIN_START_TIME_UTC } from 'cio/utils/journey-metrics';
import { Grouping } from 'cio/utils/metrics/resolution';
import SubscriptionCenterService from 'cio/services/subscription-center';
import WorkersService, { CIOWorker } from 'cio/services/workers';

const DEBOUNCE_DURATION = config.APP.shortDebounceDuration;

interface AnalysisPageArgs {
  dataTypes: AnalysisDataType[];
  onDataTypesChange: (type: AnalysisDataType) => unknown;

  tagIds: string[];
  onTagIdsChange: (ids: string[]) => unknown;

  name: string;
  onNameChange: (name: string) => unknown;

  excludeArchived: boolean;
  onExcludeArchivedChange: (excludeArchived: boolean) => unknown;

  excludeZeros: boolean;
  onExcludeZerosChange: (excludeZeros: boolean) => unknown;

  startTime: string;
  endTime: string;
  onTimeRangeChange: (start: string, end: string) => unknown;

  resolution: Periods;
  onResolutionChange: (resolution: Periods) => unknown;

  channels: string[];
  onChannelsChange: (channels: string[]) => unknown;

  measures: string[];
  onMeasuresChange: (measures: string[]) => unknown;

  sortBy: string;
  sortDirection: SortDirection;
  onSortChange: (sortBy: string, sortDirection: SortDirection) => unknown;

  tab: 'table' | 'chart';
  onTabChange: (tab: 'table' | 'chart') => unknown;

  chartType: 'individual' | 'aggregate';
  onChartTypeChange: (chartType: 'individual' | 'aggregate') => unknown;

  chartUnit: 'count' | 'percentage';
  onChartUnitChange: (chartUnit: 'count' | 'percentage') => unknown;

  chartGrouping: Grouping;
  onChartGroupingChange: (chartGrouping: Grouping) => unknown;

  chartMeasure: string;
  onChartMeasureChange: (chartMeasure: string) => unknown;
}

export default class AnalysisPage extends Component<AnalysisPageArgs> {
  @service account!: AccountService;
  @service analytics!: AnalyticsService;
  @service api!: ApiService;
  @service auth!: AuthService;
  @service environment!: EnvironmentService;
  @service exports!: ExportsService;
  @service('metrics') metricsService!: MetricsService;
  @service store!: DS.Store;
  @service subscriptionCenter!: SubscriptionCenterService;
  @service workers!: WorkersService;

  exportPendingImage = new URL('cio/assets/images/export-pending.svg', import.meta.url).toString();
  exportSuccessImage = new URL('cio/assets/images/export-success.svg', import.meta.url).toString();
  exportFailureImage = new URL('cio/assets/images/export-failure.svg', import.meta.url).toString();
  metrics: MetricItem[] = [
    {
      label: 'Messages',
      value: 'messages',
      displayOnly: true,
      items: [
        'sent',
        'delivered',
        'opened',
        'clicked',
        'clicked_to_opened',
        'converted',
        'unsubscribed',
        'topic_unsubscribed',
        'spammed',
        'failed',
        'bounced',
        'suppressed',
      ].map((measure) => {
        let value = `delivery_metrics.${measure}`;
        return {
          label: (MEASURE_LABELS as Record<string, string>)[value],
          measureType: 'delivery_metrics',
          measureName: measure,
          value,
        };
      }),
    },
    {
      label: 'Journeys',
      value: 'journeys',
      displayOnly: true,
      items: ['activated', 'in_workflow', 'converted', 'exited_early', 'finished'].map((measure) => {
        let value = `journey_metrics.${measure}`;
        return {
          label: (MEASURE_LABELS as Record<string, string>)[value],
          measureType: 'journey_metrics',
          measureName: measure,
          value,
        };
      }),
    },
  ];

  @tracked allCampaignsAndNewsletters: SimpleCampaignNewsletter[] = [];
  @tracked refinedCampaignAndNewsletterIds: string[] = [];
  @tracked selectedChartCampaignAndNewsletterIds: string[] = [];

  @tracked refineResultsModalCampaignsAndNewsletters: {
    id: string;
    name: string;
    type: string;
    icon: string;
    selected: boolean;
  }[] = [];
  @tracked refineResultsModalSearch = '';

  @computed('runReport.isRunning', 'totalMetrics.isRunning')
  get isLoading() {
    return this.runReport.isRunning || this.totalMetrics.isRunning;
  }

  @computed('refineResultsModalCampaignsAndNewsletters.@each.selected')
  get selectedRefineResultsModalCampaignsAndNewslettersCount() {
    return this.refineResultsModalCampaignsAndNewsletters.filter((c) => c.selected).length;
  }

  get filteredRefineResultsModalCampaignsAndNewsletters() {
    let searchTerm = this.refineResultsModalSearch.toLowerCase();

    if (searchTerm === '') {
      return this.refineResultsModalCampaignsAndNewsletters;
    }

    return this.refineResultsModalCampaignsAndNewsletters.filter((c) => c.name.toLowerCase().includes(searchTerm));
  }

  @tracked timeRange = LAST_30_DAYS_RANGE_PRESET_UTC.getRange();

  @tracked lastReportTimeRange = LAST_30_DAYS_RANGE_PRESET_UTC.getRange();
  @tracked lastReportQueryTimeInMilliseconds: number | null = null;

  @tracked lastReportResolution = Periods.DAYS;

  @tracked isShowingRefineResultsModal = false;
  @tracked exportModalState: 'hide' | 'export' | 'exporting' = 'hide';

  @tracked reportId?: string;
  @tracked reportIdChanged = false;
  @tracked ranReport = false;

  @tracked lastReportParams: {
    start: number;
    end: number;
    resolution: Periods;
    name: string;
    tag_ids: string[];
    status: string[];
    data_type: string[];
  } | null = null;

  @computed('environment.current.createdAt', 'args.resolution')
  get queryResolution() {
    if (this.args.resolution === Periods.ALL_TIME) {
      let environmentCreateDate = this.environment.current.createdAt;
      let createdAgoInSeconds = unixNowSeconds() - environmentCreateDate;
      let createdAgoInDays = createdAgoInSeconds / (24 * 60 * 60);
      let createdWithinDeliveryMetricsAvailabilityRange = createdAgoInDays <= MAX_CUSTOM_DAYS_COUNT;

      return createdWithinDeliveryMetricsAvailabilityRange ? Periods.DAYS : Periods.MONTHS;
    }

    return this.args.resolution;
  }

  get canExport() {
    if (!this.auth.hasPermission('journeys/exports/create')) {
      return false;
    }

    if (this.runReport.isRunning || this.runReport.performCount === 0) {
      return false;
    }

    return !isEmpty(this.rawSummedMetrics);
  }

  get exportTooltipContent() {
    if (!this.auth.hasPermission('journeys/exports/create')) {
      return 'Contact an Admin to access this feature';
    }

    if (this.runReport.isRunning) {
      return 'A report is currently being run';
    }

    if (this.runReport.performCount === 0) {
      return 'You need to run a report to export';
    }

    if (isEmpty(this.rawSummedMetrics)) {
      return 'There is no data to export';
    }

    return '';
  }

  get exportModalResultsCounts() {
    let counts: Record<string, number> = {
      campaigns: 0,
      newsletters: 0,
      broadcasts: 0,
      transactional: 0,
    };

    this.allCampaignsAndNewsletters.forEach((item) => {
      if (item.type === 'newsletter') {
        counts.newsletters = counts.newsletters + 1;
      } else if (item.type === CampaignType.APITriggered) {
        counts.broadcasts = counts.broadcasts + 1;
      } else if (item.type === 'transactional_message') {
        counts.transactional = counts.transactional + 1;
      } else {
        counts.campaigns = counts.campaigns + 1;
      }
    });

    return counts;
  }

  get canManageTags() {
    return (
      this.auth.hasPermission('journeys/campaigns/edit') ||
      this.auth.hasPermission('journeys/broadcasts/edit') ||
      this.auth.hasPermission('journeys/newsletters/edit') ||
      this.auth.hasPermission('journeys/transactional_messages/edit')
    );
  }

  constructor(owner: unknown, args: AnalysisPageArgs) {
    super(owner, args);

    if (!isEmpty(this.args.startTime) && !isEmpty(this.args.endTime)) {
      this.timeRange = {
        start: moment(this.args.startTime),
        end: moment(this.args.endTime),
      };
    }

    this.onNameChangeTrack = this.trackReportCriteriaChanged.bind(this, 'name');
    this.boundRunReport = () => {
      return this.runReport.perform();
    };
  }

  channelDropdownGroups = [
    {
      label: 'All channels',
      value: 'all_channels',
      items: [
        'email_action',
        'twilio_action',
        'push_action',
        'webhook_action',
        'slack_action',
        'urban_airship_action',
        'in_app_action',
      ]
        .filter((type: ActionType) => {
          return this.environment.enabledAction(type);
        })
        .map((type: ActionType) => {
          let info = actionInfo(type);

          return {
            label: info.name,
            value: actionTypeName(type),
            icon: info.icon,
          };
        }),
    },
  ];

  @tracked rawSummedMetrics: ProcessedMetricItem[] | null = null;
  @tracked totalMetric: {
    delivery_metrics: Record<string, number | number[]>;
    journey_metrics: Record<string, number | number[]>;
    per_channel_metrics: Record<Channel, Record<string, number | number[]>>;
    per_subscription_topic_metrics: Record<string, Record<string, number>>;
  } | null = null;

  @action
  onInsert() {
    this.runReport.perform();
  }

  totalMetrics = task({ restartable: true }, async () => {
    let totalResult = await this.workers.getWorker(CIOWorker.TotalAnalysisMetrics).postMessage([
      this.rawSummedMetrics,
      this.refinedCampaignAndNewsletterIds,
      this.args.channels,
      {
        JOURNEY_METRICS_MEASURES,
        DELIVERY_METRICS_MEASURES,
        DELIVERY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.DeliveryMetrics),
        JOURNEY_METRICS_MEASURE_DENOMINATORS: measureDenominators(MeasureType.JourneyMetrics),
        METRICS_PER_ACTION_TYPE: deliveryStatesAndActionsPerType(true),
      },
    ]);

    this.totalMetric = totalResult;
  });

  runReport = task({ restartable: true }, async (refreshData = true) => {
    if (!this.subscriptionCenter.initialized) {
      await this.subscriptionCenter.initializeTask.perform();
    }
    this.updateReportIdOnCriteriaChangeIfNecessary();
    this.analytics.track('analysis', 'report_started', {
      reportId: this.reportId,
      dataTypes: this.args.dataTypes,
      tagIds: this.args.tagIds,
      name: this.args.name,
      excludeArchived: this.args.excludeArchived,
      timeRange: this.timeRange,
      timePeriod: this.args.resolution,
    });

    this.ranReport = true;

    let status = [CampaignState.Running, CampaignState.Archived, CampaignState.Stopped];

    if (this.args.excludeArchived) {
      status = status.filter((s) => s !== CampaignState.Archived);
    }

    let start = moment.tz(`${this.timeRange.start.format('YYYY-MM-DD')} 00:00:00`, this.metricsService.metricsTz);
    let end = moment.tz(`${this.timeRange.end.format('YYYY-MM-DD')} 00:00:00`, this.metricsService.metricsTz);

    let data = {
      start: start.unix(),
      end: end.unix(),
      resolution: this.queryResolution === Periods.MONTHS ? Periods.MONTHS : Periods.DAYS,
      name: this.args.name,
      tag_ids: this.args.tagIds,
      status,
      data_type: this.args.dataTypes,
    };
    this.lastReportParams = data;

    let result = await this.metricsService.fetchAnalysisMetrics(
      this.timeRange,
      data.resolution,
      data.name,
      data.tag_ids,
      data.status,
      data.data_type,
      this.args.channels,
      {
        excludeZeros: this.args.excludeZeros,
        refreshData,
      },
      this.subscriptionCenter.topics,
    );
    this.lastReportQueryTimeInMilliseconds = result.queryTime;
    this.rawSummedMetrics = result.data;

    this.prepareCampaignAndNewsletters();

    await this.totalMetrics.perform();
    this.lastReportTimeRange = this.timeRange;
    this.lastReportResolution = this.queryResolution;
    this.reportIdChanged = false;
  });

  get rangeStartBeforeJourneyMetricsAvailability() {
    if (!this.lastReportTimeRange) {
      return false;
    }

    return (this.lastReportTimeRange.start?.unix() ?? 0) < MIN_START_TIME_UTC;
  }

  get showPagination() {
    return this.rawSummedMetrics && !this.isLoading;
  }

  @action
  prepareCampaignAndNewsletters() {
    let metrics = this.rawSummedMetrics ?? [];

    this.allCampaignsAndNewsletters = metrics
      .map(
        (result) =>
          ({
            _id: result._id,
            id: result.id,
            name: result.name,
            type: result.type,
            icon: result.icon,
          }) as SimpleCampaignNewsletter,
      )
      .sort((a, b) => {
        // Use same sorting as in sort-analysis-metrics worker.
        // We could use .sortBy, but that doesn't lowercase the values, which
        // affects the sort order
        let nameA = a.name.toLowerCase();
        let nameB = b.name.toLowerCase();

        if (nameA < nameB) {
          return -1;
        }

        if (nameA > nameB) {
          return 1;
        }

        return 0;
      });

    this.refinedCampaignAndNewsletterIds = this.allCampaignsAndNewsletters.map((m) => m.id);
    this.updateSelectedChartCampaignAndNewsletterIds(true);
  }

  @action
  updateSelectedChartCampaignAndNewsletterIds(setFirst = false) {
    if (setFirst) {
      this.selectedChartCampaignAndNewsletterIds = this.refinedCampaignAndNewsletterIds.slice(0, 7);
      return;
    }

    let currentIds = this.selectedChartCampaignAndNewsletterIds;
    let onlyExistingIds = currentIds.filter((id) => this.refinedCampaignAndNewsletterIds.includes(id));

    this.selectedChartCampaignAndNewsletterIds = onlyExistingIds;
  }

  @action
  onSelectedChannelsChanged(selectedChannels: string[]) {
    this.args.onChannelsChange(selectedChannels);

    this.runReport.perform(false);

    if (this.args.tab === 'table') {
      this.trackDisplayOptionChanged('tableChannels');
    } else if (this.args.tab === 'chart') {
      this.trackDisplayOptionChanged('chartChannels');
    }
  }

  @action
  openRefineResultsModal() {
    this.isShowingRefineResultsModal = true;

    this.refineResultsModalCampaignsAndNewsletters = this.allCampaignsAndNewsletters.map((c) => {
      return {
        ...c,
        selected: this.refinedCampaignAndNewsletterIds.includes(c.id),
      };
    });

    this.analytics.track('analysis', 'refine_results_started', {
      reportId: this.reportId,
      totalRows: this.allCampaignsAndNewsletters.length,
      selectedRows: this.refinedCampaignAndNewsletterIds.length,
      tab: this.args.tab,
    });
  }

  @action
  saveRefineResults() {
    this.isShowingRefineResultsModal = false;

    let previousSelectedRowsCount = this.refinedCampaignAndNewsletterIds.length;

    this.refinedCampaignAndNewsletterIds = this.refineResultsModalCampaignsAndNewsletters
      .filter((c) => c.selected)
      .map((c) => c.id);

    this.updateSelectedChartCampaignAndNewsletterIds();

    this.analytics.track('analysis', 'refine_results_completed', {
      reportId: this.reportId,
      totalRows: this.allCampaignsAndNewsletters.length,
      selectedRows: this.refinedCampaignAndNewsletterIds.length,
      previousSelectedRows: previousSelectedRowsCount,
    });

    this.totalMetrics.perform();
  }

  @action
  selectAllRefineResultsCampaignsAndNewsletters() {
    this.refineResultsModalCampaignsAndNewsletters = this.refineResultsModalCampaignsAndNewsletters.map((c) => ({
      ...c,
      selected: true,
    }));
  }

  @action
  selectNoneRefineResultsCampaignsAndNewsletters() {
    this.refineResultsModalCampaignsAndNewsletters = this.refineResultsModalCampaignsAndNewsletters.map((c) => ({
      ...c,
      selected: false,
    }));
  }

  @action
  changeTab(tab: 'table' | 'chart') {
    this.args.onTabChange(tab);

    if (tab === 'table') {
      // In the chart we're allowed to select any number of metrics, but in the
      // table view we only allow up to 5, so we make sure the selected metrics
      // are cut to that length when switching tabs
      this.args.onMeasuresChange(ANALYSIS_MEASURE_ORDER.filter((m) => this.args.measures.includes(m)).slice(0, 5));
    }

    this.trackDisplayOptionChanged('tab');
  }

  @action
  onSelectedMeasuresChanged(measures: string[]) {
    this.args.onMeasuresChange(measures);

    if (this.args.tab === 'table') {
      this.trackDisplayOptionChanged('tableMetrics');
    } else {
      this.trackDisplayOptionChanged('selectedLines');
    }
  }

  @action
  onDataTypesChange(type: 'campaign' | 'newsletter' | 'broadcast') {
    this.updateReportIdOnCriteriaChangeIfNecessary();

    this.args.onDataTypesChange(type);

    this.trackReportCriteriaChanged('dataType');
    this.runReport.perform();
  }

  @action
  onTagIdsChange(ids: string[]) {
    this.updateReportIdOnCriteriaChangeIfNecessary();

    this.args.onTagIdsChange(ids);

    this.trackReportCriteriaChanged('tags');
    this.runReport.perform();
  }

  @action
  onNameChange(event: InputEvent & { target: HTMLInputElement }) {
    this.updateReportIdOnCriteriaChangeIfNecessary();

    this.args.onNameChange(event.target.value);

    debounce(this, this.onNameChangeTrack, undefined, DEBOUNCE_DURATION);
    debounce(this, this.boundRunReport, DEBOUNCE_DURATION);
  }

  @action
  onExcludeArchivedChange(excludeArchived: boolean) {
    this.updateReportIdOnCriteriaChangeIfNecessary();

    this.args.onExcludeArchivedChange(excludeArchived);

    this.trackReportCriteriaChanged('excludeArchived');
    this.runReport.perform(false);
  }

  @action
  onExcludeZerosChange(excludeZeros: boolean) {
    this.args.onExcludeZerosChange(excludeZeros);

    this.runReport.perform(false);
  }

  @action
  onTimeRangeChange(timeRange: { start: moment.Moment; end: moment.Moment }, timePeriod: Periods) {
    this.timeRange = timeRange;
    this.args.onResolutionChange(timePeriod);

    this.updateReportIdOnCriteriaChangeIfNecessary();

    this.args.onTimeRangeChange(timeRange.start.format('YYYY-MM-DD'), timeRange.end.format('YYYY-MM-DD'));

    this.trackReportCriteriaChanged('timeRange');

    this.runReport.perform();
  }

  @action
  onSort(sortBy: string, sortDirection: SortDirection) {
    let oldSortBy = this.args.sortBy;
    let oldSortDirection = this.args.sortDirection;

    this.args.onSortChange(sortBy, sortDirection);

    if (sortBy !== oldSortBy) {
      this.trackDisplayOptionChanged('sortBy');
    } else if (sortDirection !== oldSortDirection) {
      this.trackDisplayOptionChanged('sortDirection');
    }
  }

  @action
  onSelectedChartCampaignAndNewsletterIdsChange(ids: string[]) {
    this.selectedChartCampaignAndNewsletterIds = ids;

    this.trackDisplayOptionChanged('selectedLines');
  }

  @action
  onChartTypeChange(chartType: 'individual' | 'aggregate') {
    this.args.onChartTypeChange(chartType);

    this.trackDisplayOptionChanged('chartType');
  }

  @action
  onChartUnitChange(chartUnit: 'count' | 'percentage') {
    this.args.onChartUnitChange(chartUnit);

    this.trackDisplayOptionChanged('chartUnit');
  }

  @action
  onChartGroupingChange(chartGrouping: Grouping, track = true) {
    this.args.onChartGroupingChange(chartGrouping);

    if (track) {
      this.trackDisplayOptionChanged('chartGrouping');
    }
  }

  @action
  onChartIndividualMeasureChange(measure: string) {
    this.args.onChartMeasureChange(measure);

    this.trackDisplayOptionChanged('chartMetric');
  }

  @action
  cancelReport() {
    this.ranReport = false;

    this.runReport.cancelAll();
    this.totalMetrics.cancelAll();
  }

  onNameChangeTrack: (editType?: string) => void;
  boundRunReport: () => void;

  @action
  trackReportCriteriaChanged(editType: string) {
    this.analytics.track('analysis', 'report_criteria_changed', {
      reportId: this.reportId,
      dataTypes: this.args.dataTypes,
      tagIds: this.args.tagIds,
      name: this.args.name,
      excludeArchived: this.args.excludeArchived,
      timeRange: this.timeRange,
      timePeriod: this.args.resolution,
      editType,
    });
  }

  @action
  updateReportIdOnCriteriaChangeIfNecessary() {
    if (!this.reportId || (this.runReport.performCount > 0 && !this.reportIdChanged)) {
      this.reportId = window.UUID.generate();
      this.reportIdChanged = true;
    }
  }

  @action
  trackReportCompleted() {
    if (!this.ranReport) {
      return;
    }

    let queryTimeInSeconds = (this.lastReportQueryTimeInMilliseconds ?? 0) / 1000;
    let queryTimeRoundedToOneDecimal = Math.round(queryTimeInSeconds * 10) / 10;

    this.analytics.track('analysis', 'report_completed', {
      reportId: this.reportId,
      dataTypes: this.args.dataTypes,
      tagIds: this.args.tagIds,
      name: this.args.name,
      excludeArchived: this.args.excludeArchived,
      timeRange: this.timeRange,
      timePeriod: this.args.resolution,
      queryTime: queryTimeRoundedToOneDecimal,
      rowCount: this.allCampaignsAndNewsletters.length,
    });
    this.ranReport = false;
    this.lastReportQueryTimeInMilliseconds = null;
  }

  @action
  trackDisplayOptionChanged(editType: string) {
    let selectedLinesCount = 0;

    if (this.args.chartType === 'individual') {
      selectedLinesCount = this.selectedChartCampaignAndNewsletterIds.length;
    } else {
      selectedLinesCount = this.args.measures.length;
    }

    this.analytics.track('analysis', 'display_option_changed', {
      reportId: this.reportId,
      editType,
      tab: this.args.tab,
      metrics: ANALYSIS_MEASURE_ORDER.filter((m) => this.args.measures.includes(m)),
      channels: this.channelDropdownGroups[0].items
        .filter((item) => this.args.channels.includes(item.value))
        .map((item) => item.value),
      sortBy: this.args.sortBy,
      sortDirection: this.args.sortDirection,
      chartType: this.args.chartType,
      chartUnit: this.args.chartUnit,
      chartGrouping: this.args.chartGrouping,
      chartMetric: this.args.chartMeasure,
      selectedLinesCount,
    });
  }

  @action
  trackChartHover(time: number) {
    let selectedLinesCount = 0;

    if (this.args.chartType === 'individual') {
      selectedLinesCount = this.selectedChartCampaignAndNewsletterIds.length;
    } else {
      selectedLinesCount = this.args.measures.length;
    }

    this.analytics.track('analysis', 'chart_hover', {
      reportId: this.reportId,
      chartType: this.args.chartType,
      chartUnit: this.args.chartUnit,
      chartGrouping: this.args.chartGrouping,
      chartMetric: this.args.chartMeasure,
      selectedLinesCount,
      time,
    });
  }

  @action
  createAnalysisExport() {
    if (!this.lastReportParams || !this.reportId) {
      return;
    }

    return this.exports.analysis({ ...this.lastReportParams, report_id: this.reportId });
  }
}
